import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { format as formatDate } from 'date-fns';

import { parseHistoryChainId } from '@/components/History/utils';

import { ADVANCED_ORDER_TYPE_LABELS } from '@/constants/advancedOrder';
import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { getTradeStatus, getTradeType, TRADE_TYPE } from '@/utils/0x/history/status';
import type { MatchaTradeHistoryEntry } from '@/utils/0x/history/types';
import type { TokenResult } from '@/utils/0x/token-registry.types';

import { CompletedTradeAmountCell } from '../cells/CompletedTradeAmountCell';
import { CompletedTradeNetworkCell } from '../cells/CompletedTradeNetworkCell';
import { CompletedTradeVolumeCell } from '../cells/CompletedTradeVolumeCell';
import type { CompletedTradeRow } from '../types';
import { getLimitDisplayPrice } from '../utils';

/**
 * Per-column responsive visibility shared by completed-trade tables, applied to
 * both the header and body cell of each column so they stay in sync.
 */
export const COMPLETED_TRADE_COLUMN_CLASS: Record<string, string> = {
  filled: 'hidden tablet:table-cell',
  network: 'hidden tablet:table-cell',
  date: 'hidden desktop:table-cell',
  // getTradeType returns lower-case labels (e.g. "cross chain"); title-case them
  // for display the way the original History table did.
  type: 'hidden tablet:table-cell capitalize',
  status: 'hidden tablet:table-cell capitalize',
};

/**
 * Normalizes a raw trade-history entry into a `CompletedTradeRow`. Limit trades
 * present maker as the sell side and taker as the buy side; every other trade
 * type is the inverse. The price is computed from the unswapped maker/taker
 * amounts so the USD/ratio logic stays correct regardless of side.
 */
export function toCompletedTradeRow(
  trade: MatchaTradeHistoryEntry,
  tokenResults: {
    makerTokenResult: TokenResult | undefined;
    takerTokenResult: TokenResult | undefined;
  },
): CompletedTradeRow {
  const chainId = parseHistoryChainId(trade.chainId);
  const destinationChainId = parseHistoryChainId(trade.destinationChainId);
  const { makerTokenResult, takerTokenResult } = tokenResults;

  const type = getTradeType({
    destinationChainId,
    liquiditySource: trade.liquiditySource,
    recipient: trade.recipient,
  });
  const isLimit = type === TRADE_TYPE.LIMIT;
  const hasExplicitFillAmounts =
    trade.filledMakerAmount !== undefined || trade.filledTakerAmount !== undefined;
  const filledSellAmount = isLimit
    ? hasExplicitFillAmounts
      ? trade.filledMakerAmount
      : trade.makerAmount
    : undefined;
  const filledBuyAmount = isLimit
    ? hasExplicitFillAmounts
      ? trade.filledTakerAmount
      : trade.takerAmount
    : undefined;
  const id = trade.orderId ?? trade.transactionHash;
  if (!id) {
    throw new Error('Completed trade is missing both orderId and transactionHash');
  }

  return {
    id,
    orderId: trade.orderId,
    transactionHash: trade.transactionHash,
    funderAddress: trade.funderAddress,
    originChainId: chainId,
    destinationChainId,
    liquiditySource: trade.liquiditySource,
    originNetworkName: chainId ? NETWORK_NAME_PER_CHAIN_ID[chainId] : undefined,
    destinationNetworkName: destinationChainId
      ? NETWORK_NAME_PER_CHAIN_ID[destinationChainId]
      : undefined,
    sellAmount: isLimit ? trade.makerAmount : trade.takerAmount,
    sellTokenSymbol: isLimit ? trade.makerTokenSymbol : trade.takerTokenSymbol,
    sellTokenResult: isLimit ? makerTokenResult : takerTokenResult,
    buyAmount: isLimit ? trade.takerAmount : trade.makerAmount,
    buyTokenSymbol: isLimit ? trade.takerTokenSymbol : trade.makerTokenSymbol,
    buyTokenResult: isLimit ? takerTokenResult : makerTokenResult,
    filledSellAmount,
    filledBuyAmount,
    isClickable: !isLimit || !!filledSellAmount || !!filledBuyAmount,
    volumeUsd: trade.volumeUSD,
    price: getLimitDisplayPrice({
      makerAmount: trade.makerAmount,
      takerAmount: trade.takerAmount,
      makerToken: trade.makerToken,
      takerToken: trade.takerToken,
      chainId,
      makerTokenResult,
      takerTokenResult,
    }),
    date: trade.timestamp ? formatDate(new Date(trade.timestamp), 'Pp') : '-',
    type: trade.advancedOrderType ? ADVANCED_ORDER_TYPE_LABELS[trade.advancedOrderType] : type,
    status: getTradeStatus({
      status: trade.state,
      liquiditySource: trade.liquiditySource,
      refundStatus: trade.refundStatus,
    }),
  };
}

const columnHelper = createColumnHelper<CompletedTradeRow>();

type CompletedTradeColumnsOptions = {
  /** Show the Filled column (limit-order surfaces). */
  showFilled?: boolean;
  /** Show the Price column (limit-order surfaces). */
  showPrice?: boolean;
  /** Show the Type column (mixed-type history surfaces). */
  showType?: boolean;
  /** Show the leading Volume column (history page). */
  showVolume?: boolean;
};

/**
 * Builds the column defs for a completed-trade table. `showVolume`, `showPrice`
 * and `showType` toggle the columns that differ between the trade page (Price,
 * no Type) and the history page (Volume and Type, no Price).
 */
export function getCompletedTradeColumns({
  showFilled = false,
  showPrice = false,
  showType = false,
  showVolume = false,
}: CompletedTradeColumnsOptions = {}): ColumnDef<CompletedTradeRow>[] {
  const columns: ColumnDef<CompletedTradeRow>[] = [];

  if (showVolume) {
    columns.push(
      columnHelper.display({
        id: 'volume',
        header: 'Volume',
        cell: ({ row }) => <CompletedTradeVolumeCell volumeUsd={row.original.volumeUsd} />,
      }),
    );
  }

  columns.push(
    columnHelper.display({
      id: 'sell',
      header: 'Sell',
      cell: ({ row }) => (
        <CompletedTradeAmountCell
          amount={row.original.sellAmount}
          symbol={row.original.sellTokenSymbol}
          token={row.original.sellTokenResult}
        />
      ),
    }),
    columnHelper.display({
      id: 'buy',
      header: 'Buy',
      cell: ({ row }) => (
        <CompletedTradeAmountCell
          amount={row.original.buyAmount}
          symbol={row.original.buyTokenSymbol}
          token={row.original.buyTokenResult}
        />
      ),
    }),
  );

  if (showFilled) {
    columns.push(
      columnHelper.display({
        id: 'filled',
        header: 'Filled',
        cell: ({ row }) => (
          <CompletedTradeAmountCell
            amount={row.original.filledSellAmount ?? 0}
            symbol={row.original.sellTokenSymbol}
            token={row.original.sellTokenResult}
          />
        ),
      }),
    );
  }

  if (showPrice) {
    columns.push(
      columnHelper.display({
        id: 'price',
        header: 'Price',
        cell: ({ row }) => row.original.price,
      }),
    );
  }

  columns.push(
    columnHelper.display({
      id: 'network',
      header: 'Network',
      cell: ({ row }) => <CompletedTradeNetworkCell row={row.original} />,
    }),
    columnHelper.display({
      id: 'date',
      header: 'Date',
      cell: ({ row }) => row.original.date,
    }),
  );

  if (showType) {
    columns.push(
      columnHelper.display({
        id: 'type',
        header: 'Type',
        cell: ({ row }) => row.original.type,
      }),
    );
  }

  columns.push(
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => row.original.status,
    }),
  );

  return columns;
}
